// components/meta-comissao/MetaComissaoTable.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MetaComissaoMensal } from '../../types/entities';
import { metaComissaoService } from '../../services/metaComissaoService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface Props {
  onEdit: (meta: MetaComissaoMensal) => void;
  onView?: (meta: MetaComissaoMensal) => void;
  onRefresh: () => void;
  onDelete?: (meta: MetaComissaoMensal) => void;
  refreshTrigger?: number;
}

export const MetaComissaoTable: React.FC<Props> = ({ 
  onEdit, 
  onView, 
  onRefresh, 
  onDelete, 
  refreshTrigger 
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<MetaComissaoMensal[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('mesAno');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<MetaComissaoMensal | null>(null);
  const [confirmMode, setConfirmMode] = useState<'delete' | 'duplicate'>('delete');
  const notifier = useNotifier();

  // Carregar metas de comissão
  const loadMetas = async (params?: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    setLoading(true);
    try {
      const currentPage = params?.page || page;
      const currentLimit = params?.limit || limit;
      const result = await metaComissaoService.list({
        page: currentPage,
        limit: currentLimit,
        sortBy: params?.sortBy || sortBy,
        sortOrder: params?.sortOrder || sortOrder,
      } as any);
      setItems(result.data || []);
      setTotalPages(result.pagination?.totalPages);
    } catch (error) {
      console.error('Erro ao carregar metas de comissão:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Efeitos

  useEffect(() => {
    loadMetas({ page, sortBy, sortOrder });
  }, [page, refreshTrigger, sortBy, sortOrder]);

  // Handlers
  // Handlers (no sorting/search UI in simplified table)
  const handleDuplicar = (meta: MetaComissaoMensal) => {
    setConfirmTarget(meta);
    setConfirmMode('duplicate');
    setConfirmOpen(true);
  };

  const handleDelete = (meta: MetaComissaoMensal) => {
    setConfirmTarget(meta);
    setConfirmMode('delete');
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!confirmTarget) return;
    setConfirmOpen(false);
    if (confirmMode === 'delete') {
      if (onDelete) {
        onDelete(confirmTarget);
      } else {
        console.warn('onDelete not provided for MetaComissaoTable');
      }
    } else if (confirmMode === 'duplicate') {
      try {
        onRefresh && onRefresh();
      } catch (err) {
        console.error('Erro ao duplicar meta:', err);
        notifier.show({ message: 'Erro ao duplicar meta', severity: 'error' });
      }
    }
    setConfirmTarget(null);
  };

  return (
    <div className="space-y-4">
      {/* Header simplified: only title and count */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between items-center">
          <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
            Metas de Comissão ({items.length})
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <label className="text-xs" style={{ color: '#3d2b56' }}>Ordenar por:</label>
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                className="px-2 py-1 border rounded-md focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212', minWidth: '140px' }}
              >
                <option value="status.nome">Status</option>
                <option value="situacao.nome">Situação</option>
                <option value="mesAno">Mês/Ano</option>
                <option value="inicioVigencia">Início Vigência</option>
                <option value="numeroDocumento">Documento</option>
                <option value="numeroApolice">Apólice</option>
                <option value="numeroEndosso">Endosso</option>
                <option value="cliente.nome">Cliente</option>
                <option value="seguradora.nome">Seguradora</option>
                <option value="produto.nome">Produto</option>
              </select>
              <button
                onClick={() => { const newOrder = sortOrder === 'asc' ? 'desc' : 'asc'; setSortOrder(newOrder); setPage(1); }}
                className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
                style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
                title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
            <button
              title="Relatório"
              onClick={() => navigate('/meta-comissao-report')}
              className="px-2 py-1 text-xs font-medium border rounded-md hover:bg-gray-50"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h6" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15V6a2 2 0 00-2-2H8l-6 6v9a2 2 0 002 2h11" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {/* Summary cards removed as requested */}
      

      {/* Cards view */}
      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="text-center py-8 text-sm text-gray-500">Carregando metas...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500">Nenhuma meta encontrada</div>
        ) : (
          items.map((meta) => (
            <div key={meta.id} className="bg-white rounded-lg shadow-sm border-l-2 p-3 hover:shadow-md transition-shadow" style={{ borderLeftColor: '#C3B4DE' }}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                <div>
                  <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Mês/Ano</div>
                  <div className="text-sm font-semibold text-gray-900">{meta.mesAno}</div>
                  {meta.empresa && (<div className="text-sm text-gray-500">{meta.empresa.nome}</div>)}
                </div>

                <div>
                  <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Meta Prêmio</div>
                  <div className="text-sm text-gray-900">{meta.valorPremio ? formatCurrency(meta.valorPremio) : '-'}</div>

                  <div className="text-gray-500 uppercase mt-2" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>% Base</div>
                  <div className="text-sm text-gray-900">{meta.percentualBase ? `${(meta.percentualBase * 100).toFixed(1)}%` : '-'}</div>
                </div>

                <div>
                  <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Meta Comissão</div>
                  <div className="text-sm text-gray-900">{meta.valorComissao ? formatCurrency(meta.valorComissao) : '-'}</div>

                  <div className="text-gray-500 uppercase mt-2" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Produtos</div>
                  <div className="text-sm text-gray-900">{meta.produtos ? `${meta.produtos.length} produto(s)` : '0 produtos'}</div>
                </div>

                <div className="flex flex-col items-end justify-between">
                  <div className="flex items-center gap-2">
                    {onView && (
                      <button onClick={() => onView(meta)} className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50" title="Visualizar">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                    <button onClick={() => {
                      const produto = meta.produtos && meta.produtos.length > 0 ? meta.produtos[0].produto : '';
                      navigate(`/meta-comissao-report?produto=${encodeURIComponent(produto)}&mesAno=${encodeURIComponent(meta.mesAno)}`);
                    }} className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50" title="Relatório do produto">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h6" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15V6a2 2 0 00-2-2H8l-6 6v9a2 2 0 002 2h11" />
                      </svg>
                    </button>
                    <button onClick={() => onEdit(meta)} className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50" title="Editar">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => handleDuplicar(meta)} className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50" title="Duplicar para outro mês">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    {onDelete && (
                      <button onClick={() => handleDelete(meta)} className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50" title="Deletar">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mt-2">Criado: {formatDate(meta.criadoEm)}</div>
                </div>
              </div>
            </div>
          ))
        )}

        {/* Paginação */}
        {totalPages && totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-700">Página {page} de {totalPages}</div>
            <div className="flex gap-2">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">Anterior</button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">Próxima</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmMode === 'delete' ? 'Remover Meta' : 'Duplicar Meta'}
        message={confirmMode === 'delete' ? 'Confirmar remoção da meta?' : `Duplicar meta ${confirmTarget?.mesAno}?`}
        confirmText={confirmMode === 'delete' ? 'Remover' : 'Duplicar'}
        cancelText="Cancelar"
        confirmButtonColor={confirmMode === 'delete' ? 'red' : undefined}
        onConfirm={handleConfirm}
        onCancel={() => { setConfirmOpen(false); setConfirmTarget(null); }}
      />
    </div>
  );
};